'use client';

import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface SlideOverPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  width?: string;
}

export default function SlideOverPanel({ open, onClose, title, subtitle, children, width = '560px' }: SlideOverPanelProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 fade-in"
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className="relative ml-auto h-full flex flex-col bg-card shadow-xl slide-in-right overflow-hidden"
        style={{ width, maxWidth: '100vw', borderLeft: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
        >
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>{title}</h2>
            {subtitle && <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted transition-colors flex-shrink-0 ml-4"
            aria-label="Close panel"
          >
            <X size={16} style={{ color: 'var(--slate)' }} />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}